import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, Calendar, Mail, Phone, Clock, Users, ArrowRight, Home, BookOpen } from 'lucide-react'
import { getContactEmail, getContactEmailLink, getStudioPhone } from '../core'

interface EnrollmentData {
  studentName: string
  studentEmail: string
  studentPhone: string
  className: string
  classId: number
  selectedDate: string
  selectedTime: string
  experience: string
  specialRequests: string
  enrollmentId: string
  enrollmentType: 'demo' | 'regular'
}

export default function Success() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [enrollmentData, setEnrollmentData] = useState<EnrollmentData | null>(null)
  const [isDemo, setIsDemo] = useState(false)

  useEffect(() => {
    const sessionIdParam = searchParams.get('session_id')
    if (sessionIdParam) {
      setSessionId(sessionIdParam)
    }

    // Get enrollment data from location state or session storage
    const data = location.state?.enrollmentData || 
                 JSON.parse(sessionStorage.getItem('enrollmentData') || 'null')
    
    if (data) {
      setEnrollmentData(data)
      setIsDemo(data.enrollmentType === 'demo')
    }
  }, [searchParams, location.state])

  if (!enrollmentData && !sessionId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading enrollment details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="gradient-bg text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {isDemo ? 'Demo Class Request Submitted! 🎉' : 'Enrollment Request Submitted! 🎉'}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {isDemo 
                ? 'Thank you for your interest! We will contact you soon to confirm your free demo session.'
                : 'Thank you for your interest! We will contact you soon to confirm your enrollment and payment details.'
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
              {/* Success Message */}
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  {isDemo ? 'Your Demo Class Request Details' : 'Enrollment Request Details'}
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  {isDemo 
                    ? 'We\'re excited to have you join us for your first dance experience! We will contact you soon to confirm the details.'
                    : 'Thank you for your interest in our dance classes! We will contact you soon to confirm your enrollment and payment details.'
                  }
                </p>
              </div>

              {/* Enrollment Details */}
              {enrollmentData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="space-y-4">
                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Users className="w-6 h-6 text-primary-500 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Student Name</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{enrollmentData.studentName}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Mail className="w-6 h-6 text-primary-500 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{enrollmentData.studentEmail}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Phone className="w-6 h-6 text-primary-500 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{enrollmentData.studentPhone}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Calendar className="w-6 h-6 text-primary-500 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Class Name</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{enrollmentData.className}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Calendar className="w-6 h-6 text-primary-500 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Date</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{enrollmentData.selectedDate}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Clock className="w-6 h-6 text-primary-500 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Time</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{enrollmentData.selectedTime}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Session ID or Enrollment ID */}
              {(sessionId || enrollmentData?.enrollmentId) && (
                <div className="bg-primary-50 dark:bg-primary-700/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-primary-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-primary-800 dark:text-primary-200">
                        <strong>Request ID:</strong> {sessionId || enrollmentData?.enrollmentId}
                      </p>
                      <p className="text-xs text-primary-600 dark:text-primary-300 mt-1">
                        Please keep this ID for your records
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Next Steps */}
              <div className={`${isDemo ? 'bg-yellow-50 dark:bg-yellow-700/20 border-yellow-200 dark:border-yellow-800' : 'bg-primary-50 dark:bg-primary-700/20 border-primary-200 dark:border-primary-800'} border rounded-lg p-6 mb-8`}>
                <h3 className={`text-lg font-semibold ${isDemo ? 'text-yellow-800 dark:text-yellow-200' : 'text-primary-800 dark:text-primary-200'} mb-4`}>
                  What's Next?
                </h3>
                <div className={`space-y-2 ${isDemo ? 'text-yellow-700 dark:text-yellow-300' : 'text-primary-700 dark:text-primary-300'}`}>
                  {isDemo ? (
                    <>
                      <div className="flex items-start">
                        <ArrowRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                        <span>We will contact you within 24 hours to confirm your demo session</span>
                      </div>
                      <div className="flex items-start">
                        <ArrowRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                        <span>Please check your email and phone for our confirmation</span>
                      </div>
                      <div className="flex items-start">
                        <ArrowRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                        <span>Wear comfortable clothes and bring a water bottle</span>
                      </div>
                      <div className="flex items-start">
                        <ArrowRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                        <span>If you enjoy the demo, you can enroll for the full course</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5 mr-3">
                          1
                        </div>
                        <span>We will contact you within 24 hours to confirm your enrollment</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5 mr-3">
                          2
                        </div>
                        <span>We will discuss payment options and finalize your schedule</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5 mr-3">
                          3
                        </div>
                        <span>Get ready for an amazing dance experience!</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Need help or have questions?
                </h3>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Mail className="w-4 h-4" />
                    <a href={getContactEmailLink()} className="hover:text-primary-600 transition-colors">
                      {getContactEmail()}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Phone className="w-4 h-4" />
                    <span>{getStudioPhone()}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/classes"
                  className="btn btn-primary flex items-center justify-center gap-2"
                >
                  <BookOpen className="w-4 h-4" />
                  {isDemo ? 'Explore All Classes' : 'View All Classes'}
                </Link>
                <button
                  onClick={() => navigate('/')}
                  className="btn btn-outline flex items-center justify-center gap-2"
                >
                  <Home className="w-4 h-4" />
                  Back to Home
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}